import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useContact } from '../../../store/hooks';
import { submitContactForm, clearError, clearSuccess } from '../../../store/slices/contactSlice';
import { showLoading, updateToast } from '../../../utils/toastr';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle, 
  MessageCircle,
  Navigation,
  HelpCircle
} from 'lucide-react';

const ContactUs = () => {
  const { loading, success, dispatch } = useContact();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    subject: '',
    message: ''
  });
  
  const headerRef = useRef(null);
  const contactInfoRef = useRef(null);
  const formRef = useRef(null);
  
  const headerInView = useInView(headerRef, { once: true });
  const contactInfoInView = useInView(contactInfoRef, { once: true });
  const formInView = useInView(formRef, { once: true });

  // Clear errors and success messages when component mounts
  useEffect(() => {
    dispatch(clearError());
    dispatch(clearSuccess());
  }, [dispatch]);

  // Reset form after successful submission
  useEffect(() => {
    if (success) {
      setFormData({
        fullName: '',
        email: '',
        mobileNumber: '',
        subject: '',
        message: ''
      });

      // Clear success state after 5 seconds
      setTimeout(() => {
        dispatch(clearSuccess());
      }, 5000);
    }
  }, [success, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for mobile number
    if (name === 'mobileNumber') {
      // Only allow numbers and limit to 10 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 10);

      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.fullName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.mobileNumber.length === 10 &&
      formData.subject.trim() !== '' &&
      formData.message.trim() !== ''
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    dispatch(clearError());
    
    // Show loading toast
    const loadingToast = showLoading('Submitting your message...');
    
    try {
      // Submit the form data to API
      await dispatch(submitContactForm(formData)).unwrap();
      
      // Update toast to success
      updateToast(
        loadingToast,
        'Message sent successfully! We\'ll get back to you soon.',
        'success'
      );
      
    } catch (error) {
      // Update toast to error
      updateToast(
        loadingToast,
        error || 'Failed to send message. Please try again.',
        'error'
      );
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['7278000038', '+91 7278000038'],
      description: 'Call us for immediate assistance',
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['scrapsectorbusiness@gmail.com'],
      description: 'Send us an email anytime',
      color: 'from-[#2F6F45] to-[#70BD7D]'
    },
    {
      icon: MapPin,
      title: 'Address',
      details: [
        'Manakpur, Thakurdass',
        'Near Railway Crossing (Peer Baba)',
        'Kalka, Pinjore, Panchkula, Haryana - 134102'
      ],
      description: 'Visit our main office',
      color: 'from-[#102018] to-[#2F6F45]'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: [
        'Monday - Saturday: 9 AM - 7 PM',
        'Sunday: 10 AM - 5 PM'
      ],
      description: 'We are here to help you',
      color: 'from-[#2F6F45] to-[#70BD7D]'
    }
  ];

  const faqs = [
    {
      question: 'What is the minimum pickup value?',
      answer: 'We have a minimum pickup value of ₹250. This ensures our service is cost-effective for both you and us.'
    },
    {
      question: 'How do you calculate scrap prices?',
      answer: 'Prices are based on current market rates, quality of materials, and quantity. We use standardized weighing and assessment methods.'
    },
    {
      question: 'Do you provide pickup in all areas?',
      answer: 'Currently, we provide service in Haryana & NCR. We are expanding to other cities soon.'
    },
    {
      question: 'How long does the pickup process take?',
      answer: 'The entire process usually takes 15-30 minutes, including weighing, assessment, and payment.'
    },
    {
      question: 'What types of scrap do you accept?',
      answer: 'We accept paper, plastic, metal, glass, e-waste, and textile scrap. Check our price list for detailed categories.'
    },
    {
      question: 'Is the pickup service free?',
      answer: 'Yes, our pickup service is completely free. We only charge for the scrap materials we collect.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F6F8F4]">

      {/* Header Section */}
      <section ref={headerRef} className="section-padding bg-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >

            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center shadow-lg shadow-[#2F6F45]/20">
                <MessageCircle className="w-8 h-8 text-[#70BD7D]" />
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-4">
              Contact{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Us
              </span>
            </h1>

            <p className="text-xl text-[#718078] max-w-3xl mx-auto leading-relaxed">
              Get in touch with us for any questions, feedback, or support. 
              We're here to help you with all your scrap collection needs.
            </p>

          </motion.div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section ref={contactInfoRef} className="section-padding">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={contactInfoInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Get in{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Touch
              </span>
            </h2>

            <p className="text-[#718078] max-w-2xl mx-auto">
              Multiple ways to reach us. Choose what works best for you.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">

            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="card text-center bg-white border border-[#DDE8DF]"
                initial={{ opacity: 0, y: 20 }}
                animate={contactInfoInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >

                <div
                  className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${info.color} flex items-center justify-center shadow-md shadow-[#2F6F45]/10`}
                >
                  <info.icon className="w-8 h-8 text-[#70BD7D]" />
                </div>

                <h3 className="text-xl font-heading font-semibold text-[#102018] mb-4">
                  {info.title}
                </h3>

                <div className="space-y-2 mb-4">
                  {info.details.map((detail, idx) => (
                    <p
                      key={idx}
                      className="text-[#718078] font-medium"
                    >
                      {detail}
                    </p>
                  ))}
                </div>

                <p className="text-sm text-[#718078] leading-relaxed">
                  {info.description}
                </p>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section ref={formRef} className="section-padding bg-white">
        <div className="container-custom">

          <div className="max-w-4xl mx-auto">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
                Send us a{' '}
                <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                  Message
                </span>
              </h2>

              <p className="text-[#718078]">
                Have a question or need assistance? We'd love to hear from you.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={formInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="card p-8 bg-white border border-[#DDE8DF]"
              >

                {success ? (
                  <div className="text-center py-12">

                    <div className="w-20 h-20 bg-[#EAF5EC] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-[#2F6F45]" />
                    </div>

                    <h3 className="text-2xl font-heading font-bold text-[#102018] mb-4">
                      Message Sent Successfully!
                    </h3>

                    <p className="text-[#718078] leading-relaxed">
                      Thank you for contacting us. We'll get back to you within 24 hours.
                    </p>

                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      <div>
                        <label
                          htmlFor="fullName"
                          className="block text-sm font-medium text-[#102018] mb-2"
                        >
                          Full Name *
                        </label>

                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your full name"
                          className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-[#102018] mb-2"
                        >
                          Email Address *
                        </label>

                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your email"
                          className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200"
                        />
                      </div>

                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      <div>
                        <label
                          htmlFor="mobileNumber"
                          className="block text-sm font-medium text-[#102018] mb-2"
                        >
                          Mobile Number *
                        </label>

                        <input
                          type="tel"
                          id="mobileNumber"
                          name="mobileNumber"
                          value={formData.mobileNumber}
                          onChange={handleInputChange}
                          required
                          minLength="10"
                          maxLength="10"
                          pattern="[0-9]{10}"
                          placeholder="Enter 10-digit mobile number"
                          className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200"
                        />

                        {formData.mobileNumber.length > 0 &&
                          formData.mobileNumber.length < 10 && (
                            <p className="text-sm text-red-500 mt-1">
                              Please enter exactly 10 digits ({formData.mobileNumber.length}/10)
                            </p>
                          )}
                      </div>

                      <div>
                        <label
                          htmlFor="subject"
                          className="block text-sm font-medium text-[#102018] mb-2"
                        >
                          Subject *
                        </label>

                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter subject of your inquiry"
                          className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200"
                        />
                      </div>

                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-[#102018] mb-2"
                      >
                        Message *
                      </label>

                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows="5"
                        placeholder="Tell us how we can help you..."
                        className="w-full px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200 resize-none"
                      />
                    </div>

                    <motion.button
                      type="submit"
                      className="btn-primary w-full flex items-center justify-center space-x-2 bg-[#2F6F45] hover:bg-[#102018] text-white"
                      disabled={loading || !isFormValid()}
                      whileHover={{
                        scale: loading || !isFormValid() ? 1 : 1.02
                      }}
                      whileTap={{
                        scale: loading || !isFormValid() ? 1 : 0.98
                      }}
                    >
                      <Send className="w-5 h-5" />
                      <span>
                        {loading ? 'Sending...' : 'Send Message'}
                      </span>
                    </motion.button>

                  </form>
                )}

              </motion.div>

              {/* Quick Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={formInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="space-y-6"
              >

                <div className="card p-8 bg-white border border-[#DDE8DF]">

                  <h3 className="text-xl font-heading font-semibold text-[#102018] mb-6">
                    Quick Contact
                  </h3>

                  <div className="space-y-4">

                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#EAF5EC] rounded-xl flex items-center justify-center">
                        <Phone className="w-6 h-6 text-[#2F6F45]" />
                      </div>

                      <div>
                        <p className="font-medium text-[#102018]">
                          Call Now
                        </p>
                        <p className="text-[#718078]">
                          7278000038
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#EAF5EC] rounded-xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-[#2F6F45]" />
                      </div>

                      <div>
                        <p className="font-medium text-[#102018]">
                          Email Us
                        </p>
                        <p className="text-[#718078]">
                          scrapsectorbusiness@gmail.com
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#EAF5EC] rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-[#2F6F45]" />
                      </div>

                      <div>
                        <p className="font-medium text-[#102018]">
                          Working Hours
                        </p>
                        <p className="text-[#718078]">
                          Mon-Sat: 9 AM - 7 PM
                        </p>
                        <p className="text-[#718078]">
                          Sun: 10 AM - 5 PM
                        </p>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="card p-8 bg-gradient-to-r from-[#EAF5EC] to-white border border-[#DDE8DF]">

                  <h3 className="text-xl font-heading font-semibold text-[#102018] mb-4">
                    Why Contact Us?
                  </h3>

                  <ul className="space-y-3">

                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2F6F45] mt-0.5" />
                      <span className="text-[#718078]">
                        Get instant quotes for your scrap
                      </span>
                    </li>

                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2F6F45] mt-0.5" />
                      <span className="text-[#718078]">
                        Schedule convenient pickup times
                      </span>
                    </li>

                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2F6F45] mt-0.5" />
                      <span className="text-[#718078]">
                        Resolve any service issues
                      </span>
                    </li>

                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#2F6F45] mt-0.5" />
                      <span className="text-[#718078]">
                        Learn about our services
                      </span>
                    </li>

                  </ul>
                </div>

              </motion.div>

            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Frequently Asked{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Questions
              </span>
            </h2>

            <p className="text-[#718078]">
              Quick answers to common questions
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="card bg-white border border-[#DDE8DF]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >

                <div className="flex items-start space-x-4">

                  <div className="w-8 h-8 bg-[#EAF5EC] rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <HelpCircle className="w-5 h-5 text-[#2F6F45]" />
                  </div>

                  <div>

                    <h3 className="text-lg font-heading font-semibold text-[#102018] mb-2">
                      {faq.question}
                    </h3>

                    <p className="text-[#718078] leading-relaxed">
                      {faq.answer}
                    </p>

                  </div>

                </div>

              </motion.div>
            ))}

          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Find{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Us
              </span>
            </h2>

            <p className="text-[#718078]">
              Visit our main office or get directions
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto">

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="card p-8 text-center bg-white border border-[#DDE8DF]"
            >

              <div className="w-20 h-20 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Navigation className="w-10 h-10 text-[#70BD7D]" />
              </div>

              <h3 className="text-2xl font-heading font-bold text-[#102018] mb-4">
                Our Location
              </h3>

              <div className="space-y-2 mb-8">
                <p className="text-[#718078]">
                  Manakpur, Thakurdass
                </p>

                <p className="text-[#718078]">
                  Near Railway Crossing (Peer Baba)
                </p>

                <p className="text-[#718078]">
                  Kalka, Pinjore, Panchkula, Haryana - 134102
                </p>
              </div>

              <motion.button
                className="btn-outline flex items-center space-x-2 mx-auto border-[#2F6F45] text-[#2F6F45] hover:bg-[#EAF5EC]"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() =>
                  window.open(
                    'https://maps.google.com/?q=Manakpur+Thakurdass+Kalka+Pinjore+Panchkula+Haryana+134102',
                    '_blank'
                  )
                }
              >
                <Navigation className="w-5 h-5" />
                <span>Get Directions</span>
              </motion.button>

            </motion.div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactUs;